import qs from 'qs';
import _ from 'lodash';
import { mapActions, mapState } from 'vuex';
import { get, eventBus } from '~/utils/adapi';
import Debug from '~/utils/debug';
import Loading from '~/components/loading/Loading.vue';
import Filters from './filters/Filters.vue';


const debug = Debug('SearchTable');

export default {
  props: {
    options: {
      type: Object,
      required: true,
    },
    openItemUrl: {
      type: Function,
      default: null,
    },
  },
  components: {
    filters: Filters,
    loading: Loading,
  },
  data() {
    // Get filter values from query
    const parsed = this.$route.hash ? qs.parse(this.$route.hash.substr(1)) : {};
    const route = this.$router.currentRoute;
    let order = {};
    if (route.name === 'campaigns') {
      order = { confirmed_at: 'desc' };
    } else if (route.name === 'creatives') {
      order = { created_at: 'desc' };
    }

    const searchDefaultValues = {
      page: Number(_.get(parsed, 'page', 1)),
      perPage: Number(_.get(parsed, 'perPage', 10)),
      sort: _.get(parsed, 'sort', 10) && _.isObject(parsed.sort) ? parsed.sort : order,
      filters: _.get(parsed, 'filter', 10) && _.isObject(parsed.filter) ? parsed.filter : {},
    };

    return {
      ...searchDefaultValues,
      initialLoading: true,
      internalOptions: this.options,
      enabledFields: [],
      enabledFieldsFilter: [],
      languageUpdateFix: false,
    };
  },
  methods: {
    ...mapActions('searchFilters', ['setVisibility']),
    ...mapActions('searchFilters', ['updateFilterData']),
    ...mapActions('searchTable', ['search']),
    updateSort(value) {
      let column = null;
      _.forOwn(this.enabledFields, (item, key) => {
        if (key === value.sortBy || item.key === value.sortBy) {
          column = item;
        }
      });
      if (column !== null) {
        const sortBy = _.get(column, 'sortableFunctionName', value.sortBy);
        const sortOrder = value.sortDesc ? 'desc' : 'asc';
        this.sort = { [sortBy]: sortOrder };
        this.filter();
      }
    },
    setUrl() {
      const request = this.$cookie.get(`filters:${this.user.id}:${this.$route.name}`);
      this.$router.replace({
        hash: request,
      });
      const parsed = this.$route.hash ? qs.parse(this.$route.hash.substr(1)) : {};
      this.page = Number(_.get(parsed, 'page', 1));
      this.perPage = Number(_.get(parsed, 'perPage', 10));
      this.sort = _.get(parsed, 'sort', 10) && _.isObject(parsed.sort) ? parsed.sort : {};
      this.filters = _.get(parsed, 'filter', 10) && _.isObject(parsed.filter) ? parsed.filter : {};
    },
    filter(updateUrl = true) {
      if (this.$cookie.get(`filters:${this.user.id}:${this.$route.name}`) && !updateUrl) {
        this.setUrl();
      }

      const request = this.createRequest();

      return this.search({ endpoint: this.options.endpoint, request })
        .then(() => {
          if (updateUrl) {
            // Change limit and offset to user friendly names
            const userUrlRequest = _.omit(request, ['limit', 'offset']);
            if (_.has(request, 'limit')) {
              userUrlRequest.perPage = request.limit;
            }
            if (_.has(request, 'offset')) {
              userUrlRequest.page = this.page;
            }

            // Remove default filter values from url
            _.forOwn(this.options.defaultFilterValues, (value, key) => {
              delete userUrlRequest.filter[key];
            });

            this.$router.replace({
              hash: `#${qs.stringify(userUrlRequest, { encode: false })}`,
            });

            this.$cookie.set(`filters:${this.user.id}:${this.$route.name}`, `#${qs.stringify(userUrlRequest, { encode: false })}`);
          }
          // set url after request needed for page refreshing
          if (this.$route.hash !== this.$cookie.get(`filters:${this.user.id}:${this.$route.name}`)) {
            this.setUrl();
          }
        });
    },
    createRequest() {
      // Send only non empty values
      const request = {};

      const offset = (this.page - 1) * this.perPage;
      if (offset > 0) request.offset = offset;
      if (this.perPage > 0) request.limit = this.perPage;

      if (!_.isEmpty(this.sort)) {
        const validSorts = Object.keys(this.sort)
          .filter(k =>
            Object.prototype.hasOwnProperty.call(this.sort, k)
            && (
              !this.enabledFields.length
              || (
                Object.prototype.hasOwnProperty.call(this.enabledFields, k)
                && this.enabledFields[k].sortable
              )
            ),
          );

        request.sort = _.pick(this.sort, validSorts);
      }

      const filters = Object.assign({}, this.options.defaultFilterValues, this.filters);
      if (!_.isEmpty(filters)) {
        request.filter = filters;
      }

      return request;
    },
    exportData(exportOptions) {
      const request = exportOptions.filtered ? this.createRequest() : {};
      request.export = exportOptions.type ? exportOptions.type : 'csv';
      request.columns = Object.keys(this.enabledTranslatedFields);
      request.columnNames = Object.keys(this.enabledTranslatedFields).reduce((mapping, k) => {
        mapping[k] = this.enabledTranslatedFields[k].label;
        return mapping;
      }, {});

      get(this.options.endpoint, request)
        .then(result => {
          const link = document.createElement('a');
          link.href = result;
          link.setAttribute('download', '');

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          this.$toast.info({ message: this.$t('pos.search-table.export-completed') });
        })
        .catch(error => {
          this.$toast.error({ message: error });
        });
    },
    getToUrl(item) {
      return this.openItemUrl instanceof Function ? this.openItemUrl(item) : '';
    },
    calculateHeight() {
      this.$nextTick(() => {
        const table = _.get(this, '$refs.itemsTable.$el', null);
        if (!table) {
          return;
        }
        const rows = Array.prototype.slice.call(table.querySelectorAll('tbody tr'));
        if (!rows) {
          return;
        }
        rows
          .forEach(tr => {
            [].forEach.call(tr.querySelectorAll('a'), a => a.style.removeProperty('height'));
            const height = tr.offsetHeight;
            if (height > 0) {
              [].forEach.call(tr.querySelectorAll('a'), a => a.style.height = `${height}px`);
            }
          });
      });
    },
    recountColumns() {
      let available = this.enabledFieldsFilter;
      if (window.innerWidth <= 768) {
        available = this.options.enabledFieldsMD || this.options.enabledFields;
      }
      if (window.innerWidth <= 576) {
        available = this.options.enabledFieldsSM || this.options.enabledFields;
      }
      const fields = {};

      _.forOwn(this.options.fields, (value, key) => {
        if (available.indexOf(key) >= 0) {
          fields[key] = value;
        }
      });

      this.enabledFields = fields;
    },
    resetFilterValues() {
      this.page = 1;
      this.perPage = 10;
      this.sort = {};
      this.filters = {};
    },
  },
  computed: {
    ...mapState('searchTable', ['data']),
    ...mapState('auth', ['user']),
    ...mapState('i18n', ['interfaceLanguage']),
    paginationText() {
      let first = this.total === 0 ? 0 : this.offset + 1;
      let last = this.offset + this.perPage;
      if (first > this.total) first = this.total;
      if (last > this.total) last = this.total;
      return this.$t('pos.search-table.showing-from-to-of', { first, last, total: this.total });
    },
    paginationOptions() {
      return [5, 10, 20, 50, 100].map(value => ({
        value,
        text: this.$t('terms.misc.per-page', { value }),
      }));
    },
    items() {
      const items = _.cloneDeep(this.data.items);

      // We need to trigger this.interfaceLanguage to preserve reactivity
      items.interfaceLanguage = this.interfaceLanguage;

      _.forEach(items, (item, key) => {
        _.forEach(this.enabledFields, (data, field) => {
          if (data.newKey === true) {
            if (data.newKeyFrom === null) {
              items[key][field] = this.data.items[key];
            } else {
              items[key][field] = _.get(this.data.items[key], data.newKeyFrom, null);
            }
          }
          data.translateCell = data.translateCell ||
              ((cell) => cell);
          items[key][field] = data.translateCell(item[field]);
        });
      });

      this.calculateHeight();
      return items || [];
    },
    offset() {
      return _.has(this.data, 'slice.offset') ? Number(this.data.slice.offset) : 0;
    },
    total() {
      return _.has(this.data, 'count.filtered') ? Number(this.data.count.filtered) : 0;
    },
    enabledTranslatedFields() {
      const fields = _.cloneDeep(this.enabledFields);
      const json = {};
      _.forOwn(fields, (value, key) => {
        value.translateColumn = value.translateColumn ||
          (() => value.label);
        fields[key].label = value.translateColumn();

        // We need to use this.interfaceLanguage to preserve reactivity
        fields[key].interfaceLanguage = this.interfaceLanguage;
      });
      this.enabledFieldsFilter.forEach((item) => {
        if (fields[item]) {
          json[item] = fields[item];
        }
      });
      return json;
    },
  },
  mounted() {
    this.setVisibility(false);
    Promise.all([
      this.updateFilterData(
        this.options.availableFilters.filter(filter =>
          (filter.getData !== null || typeof filter.filterData !== 'undefined'),
        ),
      ),
      this.filter(false),
    ])
      .then(() => this.initialLoading = false);

    window.addEventListener('resize', this.calculateHeight);
    window.addEventListener('resize', this.recountColumns);
    this.recountColumns();
    // We "manually" reload component with fresh data
    eventBus.$on('refreshSelf', () => {
      this.initialLoading = true;
      this.filter(false).then(() => this.initialLoading = false);
    });
    if (this.$cookie.get(`fields:${this.user.id}:${this.$route.name}`)) {
      _.forOwn(qs.parse(this.$cookie.get(`fields:${this.user.id}:${this.$route.name}`)), (item) => {
        this.enabledFieldsFilter.push(item);
      });
    } else {
      this.enabledFieldsFilter = this.options.enabledFields;
    }

    debug.info('mounted');
  },
  watch: {
    enabledFieldsFilter: {
      handler: function watchInternalOptions(enabledFields) {
        this.$cookie.set(`fields:${this.user.id}:${this.$route.name}`, qs.stringify(enabledFields));
        const fields = {};
        _.forOwn(this.options.fields, (value, key) => {
          if (enabledFields.indexOf(key) >= 0) {
            fields[key] = value;
          }
        });
        this.enabledFields = fields;
      },
    },
    'options.defaultFilterValues': 'filter',
    interfaceLanguage: function fix() {
      // Filters, headers and table data will be force updated
      this.languageUpdateFix = true;
      this.$nextTick(() => this.languageUpdateFix = false);
    },
  },
  ready: () => {
    window.addEventListener('resize', this.recountColumns);
  },
  beforeDestroy: () => {
    window.removeEventListener('resize', this.calculateHeight);
  },
};



// WEBPACK FOOTER //
// ./src/gotu-vue-common/src/components/search-table/SearchTable.js